import { useEffect, useState } from 'react';
import { api } from '../api';
import { OPPORTUNITY_EVENT_LABELS, OPPORTUNITY_STATUS_COLORS, type JourneyResponse } from '../types';
import { formatDateTime, formatDuration, formatNumber } from '../format';
import { Dialog } from './Dialog';

/** A dot colour per event type, reusing the validated status hues. */
const EVENT_COLORS: Record<string, string> = {
  activity: 'var(--accent)',
  created: 'var(--outcome-voicemail)',
  stage_changed: 'var(--accent)',
  status_changed: OPPORTUNITY_STATUS_COLORS.won,
  value_changed: OPPORTUNITY_STATUS_COLORS.abandoned,
  assigned_changed: 'var(--ink-muted)',
  deleted: OPPORTUNITY_STATUS_COLORS.lost,
};

function describe(event: JourneyResponse['timeline'][number]): string {
  // An activity entry carries HighLevel's own wording; nothing to improve on.
  if (event.kind === 'activity') return event.body ?? 'Activity recorded in HighLevel';
  const from = event.from ?? '—';
  const to = event.to ?? '—';
  switch (event.type) {
    case 'created':
      return event.to ? `Created in ${to}` : 'Created';
    case 'stage_changed':
      return `${from} → ${to}`;
    case 'status_changed':
      return `${from} → ${to}`;
    case 'value_changed':
      return `${from} → ${to}`;
    case 'assigned_changed':
      return `${from} → ${to}`;
    case 'deleted':
      return 'Deleted in HighLevel';
    default:
      return `${from} → ${to}`;
  }
}

export function JourneyDrawer({ opportunityId, onClose }: { opportunityId: string; onClose: () => void }) {
  const [data, setData] = useState<JourneyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .opportunityJourney(opportunityId)
      .then(setData)
      .catch((cause) => setError(String(cause)));
  }, [opportunityId]);

  const title = data?.opportunity.name ?? 'Opportunity journey';

  return (
    <Dialog title={title} onClose={onClose}>
      {error ? <div className="banner error">{error}</div> : null}
      {!data && !error ? <p className="empty">Loading journey…</p> : null}

      {data ? (
        <div className="stack">
          <div className="dialog-grid">
            <div>
              <div className="cell-sub">Contact</div>
              {data.opportunity.contactUrl ? (
                <a href={data.opportunity.contactUrl} target="_blank" rel="noreferrer">
                  {data.opportunity.contactName ?? 'Open contact'}
                </a>
              ) : (
                <span>{data.opportunity.contactName ?? '—'}</span>
              )}
            </div>
            <div>
              <div className="cell-sub">Pipeline · stage</div>
              <span>
                {data.opportunity.pipelineName ?? '—'} · {data.opportunity.stageName ?? '—'}
              </span>
            </div>
            <div>
              <div className="cell-sub">Owner</div>
              <span>{data.opportunity.assignedName ?? 'Unassigned'}</span>
            </div>
            <div>
              <div className="cell-sub">Value</div>
              <span>{formatNumber(data.opportunity.monetaryValue)}</span>
            </div>
          </div>

          <div className="stack" style={{ gap: 0 }}>
            {data.timeline.map((event) => (
              <div className="journey-row" key={event.id}>
                <span className="journey-dot" style={{ background: EVENT_COLORS[event.type] ?? 'var(--accent)' }} />
                <div className="journey-body">
                  <div className="spread">
                    <span className="cell-strong">
                      {event.kind === 'activity'
                        ? 'Activity'
                        : (OPPORTUNITY_EVENT_LABELS[event.type] ?? event.type)}
                    </span>
                    <span className="note-meta">{formatDateTime(event.occurredAt)}</span>
                  </div>
                  <div className="note-body">{describe(event)}</div>
                  <div className="cell-sub">
                    {event.msUntilNext !== null
                      ? `${formatDuration(Math.round(event.msUntilNext / 1000))} until the next change · `
                      : ''}
                    {event.detectedBy === 'webhook'
                      ? 'from a live webhook'
                      : event.detectedBy === 'activity'
                        ? 'from HighLevel’s activity log'
                        : 'from HighLevel’s own timestamp'}
                    {event.actorName
                      ? ` · by ${event.actorName}${
                          event.actorSource === 'activity' ? ' (matched from the activity log)' : ''
                        }`
                      : ''}
                  </div>
                </div>
              </div>
            ))}
            {data.timeline.length === 0 ? (
              <p className="empty">
                No transitions recorded yet. The journey builds from the point this dashboard started
                watching, so an opportunity that has not moved since then has nothing to show.
              </p>
            ) : null}
          </div>

          <p className="small muted">
            {data.attributed > 0 ? (
              <>
                {data.attributed} of {data.timeline.length} entries name a person. Attribution comes
                from HighLevel’s conversation activity log, matched to the change by time — the
                opportunity endpoints and webhooks carry no actor themselves, so an entry with no
                matching activity row stays unattributed.
              </>
            ) : (
              <>
                No entry names a person yet. Attribution comes from HighLevel’s conversation activity
                log, which is synced separately — if this stays empty, check{' '}
                <code>/api/journeys/activity/sample</code> to see whether activity entries are
                arriving for this sub-account.
              </>
            )}{' '}
            Times come from HighLevel’s own <code>lastStageChangeAt</code> and{' '}
            <code>lastStatusChangeAt</code>, so they are when the change happened.            {data.showsAllActivity ? null : (
              <>
                {' '}
                This contact has other opportunities, and HighLevel writes activity against the
                contact rather than the record — so entries that could not be matched to a change
                here are withheld rather than shown against the wrong one.
              </>
            )}
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}
